import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Shield, Database, Server, Globe, Mail, Bell,
  Save, RefreshCw, CheckCircle, AlertTriangle, Lock,
  Eye, EyeOff, ChevronDown, ChevronUp, CreditCard, Key
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { callStaffLambdaDirect } from '../../utils/authFallback';

const defaultSettings = {
  general: {
    companyName: 'Receevit®',
    companyEmail: 'admin@receevit.com',
    timezone: 'Europe/London',
    currency: 'GBP',
    dateFormat: 'DD/MM/YYYY'
  },
  email: {
    smtpHost: 'smtp.gmail.com',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: 'noreply@receevit.com',
    fromName: 'Receevit®'
  },
  payment: {
    stripePublicKey: '',
    stripeSecretKey: '',
    webhookSecret: '',
    defaultCurrency: 'GBP',
    paymentTerms: '30'
  },
  security: {
    sessionTimeout: '24',
    maxLoginAttempts: '5',
    require2FA: false,
    passwordMinLength: '8',
    passwordRequireSpecial: true
  },
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    systemAlerts: true,
    maintenanceMode: false
  }
};

const STAFF_SETTINGS_PATH = '/api/staff/settings';

const MobileStaffSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    email: false,
    payment: false,
    security: false,
    notifications: false
  });
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    fetchSettings();
  }, [user?.id]);

  const mergeSettings = (incoming = {}) => ({
    general: { ...defaultSettings.general, ...(incoming.general || {}) },
    email: { ...defaultSettings.email, ...(incoming.email || {}) },
    payment: { ...defaultSettings.payment, ...(incoming.payment || {}) },
    security: { ...defaultSettings.security, ...(incoming.security || {}) },
    notifications: { ...defaultSettings.notifications, ...(incoming.notifications || {}) }
  });

  const callStaffSettingsApi = async ({ method = 'get', payload = {} } = {}) => {
    const methodLower = String(method || 'get').toLowerCase();
    const methodUpper = methodLower.toUpperCase();
    const staffHeaders = user?.id
      ? {
          'staff-id': user.id,
          'user-id': user.id
        }
      : {};

    const executeDirect = () =>
      callStaffLambdaDirect({
        path: STAFF_SETTINGS_PATH,
        method: methodUpper,
        payload,
        additionalHeaders: staffHeaders
      });

    try {
      return await executeDirect();
    } catch (directError) {
      console.warn('[MobileStaffSettings] direct staff settings call failed, falling back to API proxy', directError?.message || directError);
      try {
        switch (methodLower) {
          case 'get':
            return (await api.get(STAFF_SETTINGS_PATH, { __skipAuthTokenPurge: true })).data;
          case 'post':
            return (await api.post(STAFF_SETTINGS_PATH, payload, { __skipAuthTokenPurge: true })).data;
          case 'put':
            return (await api.put(STAFF_SETTINGS_PATH, payload, { __skipAuthTokenPurge: true })).data;
          case 'delete':
            return (await api.delete(STAFF_SETTINGS_PATH, { data: payload, __skipAuthTokenPurge: true })).data;
          default:
            return (
              await api.request({
                url: STAFF_SETTINGS_PATH,
                method: methodLower,
                data: payload,
                __skipAuthTokenPurge: true
              })
            ).data;
        }
      } catch (fallbackError) {
        console.error('[MobileStaffSettings] proxy staff settings call failed', fallbackError);
        throw fallbackError;
      }
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await callStaffSettingsApi({ method: 'get' });
      const payload =
        response?.settings ||
        response?.data?.settings ||
        response?.data ||
        response;

      if (payload) {
        setSettings(mergeSettings(payload));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSaveSection = async (section) => {
    try {
      setSaving(true);
      await callStaffSettingsApi({
        method: 'put',
        payload: {
          [section]: settings[section]
        }
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const settingsSections = [
    {
      id: 'general',
      title: 'General Settings',
      icon: Settings,
      description: 'Basic system configuration'
    },
    {
      id: 'email',
      title: 'Email Settings',
      icon: Mail,
      description: 'SMTP and email configuration'
    },
    {
      id: 'payment',
      title: 'Payment Settings',
      icon: CreditCard,
      description: 'Stripe and payment configuration'
    },
    {
      id: 'security',
      title: 'Security Settings',
      icon: Shield,
      description: 'Security policies and access control'
    },
    {
      id: 'notifications',
      title: 'Notification Settings',
      icon: Bell,
      description: 'System alerts and notifications'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Staff Settings</h1>
            <p className="text-sm text-gray-600 mt-1">System configuration</p>
          </div>
          <button
            onClick={fetchSettings}
            className="p-2 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="p-4 space-y-3">
        {settingsSections.map((section, index) => {
          const Icon = section.icon;
          const isExpanded = expandedSections[section.id];
          
          return (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Icon className="w-5 h-5 text-[#1F11ED]" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-xs text-gray-600">{section.description}</p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {/* Section Content */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-4 pb-4 border-t border-gray-200"
                >
                  <div className="pt-4 space-y-4">
                    {/* General Settings */}
                    {section.id === 'general' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <input
                            type="text"
                            value={settings.general.companyName}
                            onChange={(e) => updateSetting('general', 'companyName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Email</label>
                          <input
                            type="email"
                            value={settings.general.companyEmail}
                            onChange={(e) => updateSetting('general', 'companyEmail', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                          <select
                            value={settings.general.timezone}
                            onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          >
                            <option value="Europe/London">London (GMT)</option>
                            <option value="America/New_York">New York (EST)</option>
                            <option value="Europe/Paris">Paris (CET)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                          <select
                            value={settings.general.currency}
                            onChange={(e) => updateSetting('general', 'currency', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          >
                            <option value="GBP">British Pound (GBP)</option>
                            <option value="USD">US Dollar (USD)</option>
                            <option value="EUR">Euro (EUR)</option>
                          </select>
                        </div>
                        <button
                          onClick={() => handleSaveSection('general')}
                          disabled={saving}
                          className="w-full bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Saving...' : 'Save General Settings'}</span>
                        </button>
                      </>
                    )}

                    {/* Email Settings */}
                    {section.id === 'email' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
                          <input
                            type="text"
                            value={settings.email.smtpHost}
                            onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                          <input
                            type="text"
                            value={settings.email.smtpPort}
                            onChange={(e) => updateSetting('email', 'smtpPort', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                          <input
                            type="email"
                            value={settings.email.fromEmail}
                            onChange={(e) => updateSetting('email', 'fromEmail', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                          <input
                            type="text"
                            value={settings.email.fromName}
                            onChange={(e) => updateSetting('email', 'fromName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={() => handleSaveSection('email')}
                          disabled={saving}
                          className="w-full bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Saving...' : 'Save Email Settings'}</span>
                        </button>
                      </>
                    )}

                    {/* Payment Settings */}
                    {section.id === 'payment' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Public Key</label>
                          <div className="relative">
                            <input
                              type={showPasswords.stripePublic ? 'text' : 'password'}
                              value={settings.payment.stripePublicKey}
                              onChange={(e) => updateSetting('payment', 'stripePublicKey', e.target.value)}
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                              placeholder="pk_..."
                            />
                            <button
                              onClick={() => setShowPasswords(prev => ({ ...prev, stripePublic: !prev.stripePublic }))}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            >
                              {showPasswords.stripePublic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Secret Key</label>
                          <div className="relative">
                            <input
                              type={showPasswords.stripeSecret ? 'text' : 'password'}
                              value={settings.payment.stripeSecretKey}
                              onChange={(e) => updateSetting('payment', 'stripeSecretKey', e.target.value)}
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                              placeholder="sk_..."
                            />
                            <button
                              onClick={() => setShowPasswords(prev => ({ ...prev, stripeSecret: !prev.stripeSecret }))}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            >
                              {showPasswords.stripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
                          <select
                            value={settings.payment.defaultCurrency}
                            onChange={(e) => updateSetting('payment', 'defaultCurrency', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                          >
                            <option value="GBP">British Pound (GBP)</option>
                            <option value="USD">US Dollar (USD)</option>
                            <option value="EUR">Euro (EUR)</option>
                          </select>
                        </div>
                        <button
                          onClick={() => handleSaveSection('payment')}
                          disabled={saving}
                          className="w-full bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Saving...' : 'Save Payment Settings'}</span>
                        </button>
                      </>
                    )}

                    {/* Security Settings */}
                    {section.id === 'security' && (
                      <>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">Require 2FA</p>
                              <p className="text-xs text-gray-600">Two-factor authentication for all staff</p>
                            </div>
                            <button
                              onClick={() => updateSetting('security', 'require2FA', !settings.security.require2FA)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.security.require2FA ? 'bg-[#1F11ED]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.security.require2FA ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">Password Requires Special Char</p>
                              <p className="text-xs text-gray-600">Enforce special characters in passwords</p>
                            </div>
                            <button
                              onClick={() => updateSetting('security', 'passwordRequireSpecial', !settings.security.passwordRequireSpecial)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.security.passwordRequireSpecial ? 'bg-[#1F11ED]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.security.passwordRequireSpecial ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Session Timeout (hours)</label>
                            <input
                              type="number"
                              value={settings.security.sessionTimeout}
                              onChange={(e) => updateSetting('security', 'sessionTimeout', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Login Attempts</label>
                            <input
                              type="number"
                              value={settings.security.maxLoginAttempts}
                              onChange={(e) => updateSetting('security', 'maxLoginAttempts', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => handleSaveSection('security')}
                          disabled={saving}
                          className="w-full bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Saving...' : 'Save Security Settings'}</span>
                        </button>
                      </>
                    )}

                    {/* Notification Settings */}
                    {section.id === 'notifications' && (
                      <>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">Email Notifications</p>
                              <p className="text-xs text-gray-600">Send email alerts to staff</p>
                            </div>
                            <button
                              onClick={() => updateSetting('notifications', 'emailNotifications', !settings.notifications.emailNotifications)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.notifications.emailNotifications ? 'bg-[#1F11ED]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.notifications.emailNotifications ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">System Alerts</p>
                              <p className="text-xs text-gray-600">Critical system notifications</p>
                            </div>
                            <button
                              onClick={() => updateSetting('notifications', 'systemAlerts', !settings.notifications.systemAlerts)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.notifications.systemAlerts ? 'bg-[#1F11ED]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.notifications.systemAlerts ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">SMS Notifications</p>
                              <p className="text-xs text-gray-600">Send SMS alerts</p>
                            </div>
                            <button
                              onClick={() => updateSetting('notifications', 'smsNotifications', !settings.notifications.smsNotifications)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.notifications.smsNotifications ? 'bg-[#1F11ED]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.notifications.smsNotifications ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                            <div>
                              <p className="font-medium text-red-900">Maintenance Mode</p>
                              <p className="text-xs text-red-600">System will be unavailable</p>
                            </div>
                            <button
                              onClick={() => updateSetting('notifications', 'maintenanceMode', !settings.notifications.maintenanceMode)}
                              className={`w-12 h-6 rounded-full relative transition-colors ${
                                settings.notifications.maintenanceMode ? 'bg-red-600' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                                settings.notifications.maintenanceMode ? 'right-0.5' : 'left-0.5'
                              }`}></div>
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => handleSaveSection('notifications')}
                          disabled={saving}
                          className="w-full bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Saving...' : 'Save Notification Settings'}</span>
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileStaffSettings;
